/**
 * QuestionnaireDetailPage - View and edit questionnaire items
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getQuestionnaire,
  updateQuestionnaireItem,
  reprocessItem,
  exportQuestionnaire,
  markItemsSavedToQA,
} from '../services/questionnaireService';
import { updateQADatabaseFromResponses, getURLMappings } from '../services/api';
import { QuestionnaireWithItems, QuestionnaireItem, UpdateItemRequest, SubscriptionTier } from '../types';
import MainLayout from '../components/MainLayout';
import QuestionnaireItemRow, { URLMapping } from '../components/questionnaire/QuestionnaireItemRow';
import './QuestionnaireDetailPage.css';

const QuestionnaireDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, userContext, signOut } = useAuth();
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingItems, setSavingItems] = useState<Set<string>>(new Set());
  const [addedToQAItems, setAddedToQAItems] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [urlMappings, setUrlMappings] = useState<URLMapping[]>([]);

  // Save to Q&A Bank state
  const [isSavingToQA, setIsSavingToQA] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [customerNameForSave, setCustomerNameForSave] = useState('');

  // Export warning modal state
  const [showExportWarning, setShowExportWarning] = useState(false);
  const [exportWarningData, setExportWarningData] = useState<{
    notReviewed: number;
    uncertain: number;
    saveable: number;
  } | null>(null);

  // Filter state: 'all' | 'approved' | 'edited' | 'not-reviewed' | 'uncertain' | 'saveable' | 'saved'
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'edited' | 'not-reviewed' | 'uncertain' | 'saveable' | 'saved'>('all');

  const projectId = user?.project || '';
  const projectName = userContext?.project?.company_name || '';
  const userRole = user?.role || userContext?.membership?.role;
  const displayName = user?.fullName || userContext?.profile?.full_name || user?.email;
  const subscriptionTier = (userContext?.project?.subscription_tier || 'free') as SubscriptionTier;

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const loadQuestionnaire = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getQuestionnaire(id);
      setQuestionnaire(data);
      setError(null);

      // Initialize addedToQAItems from saved items in DB
      const savedItemIds = new Set(
        data.items
          .filter(item => item.is_saved_to_qa)
          .map(item => item.id)
      );
      setAddedToQAItems(savedItemIds);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadQuestionnaire();
  }, [loadQuestionnaire]);

  // Fetch URL mappings once for all items (avoids N+1 API calls)
  useEffect(() => {
    if (!projectId) return;
    const loadMappings = async () => {
      try {
        const result = await getURLMappings(projectId);
        if (result.mappings) {
          setUrlMappings(result.mappings);
        }
      } catch (error) {
        console.error('Failed to load URL mappings:', error);
      }
    };
    loadMappings();
  }, [projectId]);

  // Auto-refresh while processing (only if tab is visible)
  // Also poll if any individual item is pending (for regeneration)
  const hasPendingItems = questionnaire?.items?.some(item => item.status === 'pending' || item.status === 'processing');

  useEffect(() => {
    if (questionnaire?.status !== 'processing' && !hasPendingItems) return;

    const poll = () => {
      if (document.visibilityState === 'visible') {
        loadQuestionnaire();
      }
    };

    const interval = setInterval(poll, 3000); // Poll faster (3s) for better UX
    return () => clearInterval(interval);
  }, [questionnaire?.status, hasPendingItems, loadQuestionnaire]);

  const handleSaveItem = useCallback(async (itemId: string, updates: UpdateItemRequest) => {
    setSavingItems(prev => new Set(prev).add(itemId));
    try {
      await updateQuestionnaireItem(itemId, {
        ...updates,
        edited_by: user?.email,
        edited_by_name: user?.fullName || user?.email, // Fallback to email if no name
      });
      // Update local state
      setQuestionnaire(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            item.id === itemId
              ? { ...item, ...updates, is_manually_edited: true }
              : item
          ),
        };
      });
    } catch (err) {
      console.error('Failed to save item:', err);
    } finally {
      setSavingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [user?.id]);

  const handleReprocess = useCallback(async (itemId: string, additionalContext?: string) => {
    if (!id) return;
    try {
      // Pass user info so backend can mark as edited
      await reprocessItem(id, itemId, additionalContext, user?.email, user?.fullName || user?.email);
      // Mark item as pending locally
      setQuestionnaire(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            item.id === itemId ? { ...item, status: 'pending' as const } : item
          ),
        };
      });
    } catch (err) {
      console.error('Failed to reprocess item:', err);
    }
  }, [id, user?.email, user?.fullName]);

  const handleUpdateSources = useCallback(async (itemId: string, evidence: string[]) => {
    setSavingItems(prev => new Set(prev).add(itemId));
    try {
      await updateQuestionnaireItem(itemId, {
        evidence,
        edited_by: user?.email,
        edited_by_name: user?.fullName || user?.email, // Fallback to email if no name
      });
      // Update local state
      setQuestionnaire(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            item.id === itemId
              ? { ...item, evidence, is_manually_edited: true }
              : item
          ),
        };
      });
    } catch (err) {
      console.error('Failed to update sources:', err);
    } finally {
      setSavingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [user?.id]);

  const handleAddToQA = useCallback(async (item: QuestionnaireItem, customerName: string) => {
    if (!projectId) return;

    // Convert item to the format expected by updateQADatabaseFromResponses
    const qaEntry = {
      question: item.original_query,
      answer: item.response || '',
    };

    try {
      await updateQADatabaseFromResponses(projectId, [qaEntry], customerName);
      setAddedToQAItems(prev => new Set(prev).add(item.id));
    } catch (err) {
      console.error('Failed to add to Q&A:', err);
      throw err;
    }
  }, [projectId]);

  // Check for incomplete items before export
  const handleExportClick = () => {
    if (!questionnaire) return;

    const counts = getItemCounts();
    const hasIncompleteItems = counts.notReviewed > 0 || counts.uncertain > 0 || counts.saveable > 0;

    if (hasIncompleteItems) {
      // Show warning modal
      setExportWarningData({
        notReviewed: counts.notReviewed,
        uncertain: counts.uncertain,
        saveable: counts.saveable,
      });
      setShowExportWarning(true);
    } else {
      // No warnings, export directly
      performExport();
    }
  };

  // Actually perform the export
  const performExport = async () => {
    if (!questionnaire) return;
    setShowExportWarning(false);
    try {
      // Pass URL mappings to backend so it can display proper filenames in Sources column
      await exportQuestionnaire(
        questionnaire.id,
        `${questionnaire.client_name}_${questionnaire.original_filename}`,
        urlMappings
      );
    } catch (err) {
      setError('Failed to export questionnaire');
    }
  };

  // Navigate to filter and close warning modal
  const handleWarningAction = (filter: 'not-reviewed' | 'uncertain' | 'saveable') => {
    setShowExportWarning(false);
    setStatusFilter(filter);
  };

  const getProgress = () => {
    if (!questionnaire || questionnaire.total_items === 0) return 0;
    return Math.round((questionnaire.completed_items / questionnaire.total_items) * 100);
  };

  // Compute counts for approved, edited, uncertain, saved, and not reviewed items
  const getItemCounts = useCallback(() => {
    if (!questionnaire?.items) {
      return { approved: 0, edited: 0, uncertain: 0, notReviewed: 0, saveable: 0, saved: 0 };
    }

    let approved = 0;
    let edited = 0;
    let uncertain = 0;
    let notReviewed = 0;
    let saved = 0;
    let saveableNotYetSaved = 0; // Items that can be saved but haven't been yet

    for (const item of questionnaire.items) {
      if (item.status !== 'completed') continue; // Skip pending/failed items

      // Count saved items
      if (item.is_saved_to_qa) {
        saved++;
      }

      if (item.is_uncertain) {
        uncertain++;
      } else if (item.is_approved === true) {
        approved++;
        // Only count as saveable if not already saved
        if (!item.is_saved_to_qa) {
          saveableNotYetSaved++;
        }
      } else if (item.is_manually_edited || item.is_approved === false) {
        edited++;
        // Only count as saveable if not already saved
        if (!item.is_saved_to_qa) {
          saveableNotYetSaved++;
        }
      } else {
        notReviewed++;
      }
    }

    return {
      approved,
      edited,
      uncertain,
      notReviewed,
      saveable: saveableNotYetSaved, // Items ready to save (excluding already saved)
      saved
    };
  }, [questionnaire?.items]);

  // Get items that should be saved to Q&A bank (approved or edited, but NOT already saved)
  const getSaveableItems = useCallback((): QuestionnaireItem[] => {
    if (!questionnaire?.items) return [];
    return questionnaire.items.filter(item =>
      item.status === 'completed' &&
      !item.is_saved_to_qa && // Exclude already saved items
      (item.is_approved === true || item.is_manually_edited || item.is_approved === false)
    );
  }, [questionnaire?.items]);

  // Filter items by status filter
  const filterItemsByStatus = useCallback((items: QuestionnaireItem[]): QuestionnaireItem[] => {
    if (statusFilter === 'all') return items;

    return items.filter(item => {
      // Non-completed items are excluded from status filters
      if (item.status !== 'completed') return false;

      switch (statusFilter) {
        case 'approved':
          return item.is_approved === true && !item.is_uncertain;
        case 'edited':
          return (item.is_manually_edited || item.is_approved === false) && item.is_approved !== true && !item.is_uncertain;
        case 'uncertain':
          return item.is_uncertain === true;
        case 'not-reviewed':
          return !item.is_approved && !item.is_manually_edited && !item.is_uncertain;
        case 'saveable':
          return (item.is_approved === true || item.is_manually_edited || item.is_approved === false) && !item.is_uncertain;
        case 'saved':
          return item.is_saved_to_qa === true;
        default:
          return true;
      }
    });
  }, [statusFilter]);

  // Toggle filter - click again to clear
  const handleFilterClick = (filter: typeof statusFilter) => {
    setStatusFilter(current => current === filter ? 'all' : filter);
  };

  // Handle save to Q&A bank
  const handleSaveToQABank = async () => {
    if (!projectId || !questionnaire) return;

    const saveableItems = getSaveableItems();
    if (saveableItems.length === 0) return;

    setIsSavingToQA(true);
    setSaveSuccess(null);

    try {
      // Convert items to Q&A entries
      const entries = saveableItems.map(item => ({
        question: item.original_query,
        answer: item.response || ''
      }));

      // Use the customer name from modal, or fall back to questionnaire client name
      const customerName = customerNameForSave.trim() || questionnaire.client_name;

      const result = await updateQADatabaseFromResponses(projectId, entries, customerName);

      // Mark items as saved in the database
      const itemIds = saveableItems.map(item => item.id);
      await markItemsSavedToQA(itemIds, user?.email, user?.fullName);

      // Update local state to reflect saved status
      setQuestionnaire(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            itemIds.includes(item.id)
              ? {
                  ...item,
                  is_saved_to_qa: true,
                  saved_to_qa_at: new Date().toISOString(),
                  saved_to_qa_by: user?.email,
                  saved_to_qa_by_name: user?.fullName,
                }
              : item
          ),
        };
      });

      // Mark all saved items as added to QA (for UI)
      setAddedToQAItems(prev => {
        const next = new Set(prev);
        saveableItems.forEach(item => next.add(item.id));
        return next;
      });

      setSaveSuccess(`✅ Saved ${result.added_count} Q&A pairs to knowledge bank`);
      setShowSaveModal(false);
      setCustomerNameForSave('');

      // Clear success message after 5 seconds
      setTimeout(() => setSaveSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save to Q&A bank');
    } finally {
      setIsSavingToQA(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="detail-page loading">Loading questionnaire...</div>;
    }

    if (error || !questionnaire) {
      return (
        <div className="detail-page error">
          <p>{error || 'Questionnaire not found'}</p>
          <button onClick={() => navigate('/questionnaires')}>← Back to List</button>
        </div>
      );
    }

    // Filter items by search term and status filter
    const searchFilteredItems = questionnaire.items.filter(item =>
      item.original_query.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.response || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    const filteredItems = filterItemsByStatus(searchFilteredItems);

    const counts = getItemCounts();

    return (
      <div className="questionnaire-detail-page">
        <div className="detail-header">
          <button className="back-btn" onClick={() => navigate('/questionnaires')}>
            ← Back
          </button>
          <div className="header-info">
            <h1>{questionnaire.client_name}</h1>
            <span className={`status-badge status-${questionnaire.status}`}>
              {questionnaire.status}
            </span>
          </div>
          <div className="header-actions">
            <input
              type="text"
              placeholder="🔍 Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button
              className="save-qa-btn"
              onClick={() => setShowSaveModal(true)}
              disabled={counts.saveable === 0 || isSavingToQA}
              title={counts.saveable === 0 ? 'No approved or edited items to save' : `Save ${counts.saveable} items to Q&A bank`}
            >
              {isSavingToQA ? '💾 Saving...' : `💾 Save to Q&A (${counts.saveable})`}
            </button>
            <button onClick={handleExportClick}>📥 Export CSV</button>
          </div>
        </div>

        {/* Review Stats Bar - Clickable Filters */}
        <div className="review-stats-bar">
          <button
            className={`stat-item approved ${statusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => handleFilterClick('approved')}
            title="Click to filter approved items"
          >
            <span className="stat-icon">✓</span>
            <span className="stat-count">{counts.approved}</span>
            <span className="stat-label">Approved</span>
          </button>
          <button
            className={`stat-item edited ${statusFilter === 'edited' ? 'active' : ''}`}
            onClick={() => handleFilterClick('edited')}
            title="Click to filter edited items"
          >
            <span className="stat-icon">✎</span>
            <span className="stat-count">{counts.edited}</span>
            <span className="stat-label">Edited</span>
          </button>
          <button
            className={`stat-item uncertain ${statusFilter === 'uncertain' ? 'active' : ''}`}
            onClick={() => handleFilterClick('uncertain')}
            title="Click to filter items marked as uncertain"
          >
            <span className="stat-icon">?</span>
            <span className="stat-count">{counts.uncertain}</span>
            <span className="stat-label">Not Sure</span>
          </button>
          <button
            className={`stat-item not-reviewed ${statusFilter === 'not-reviewed' ? 'active' : ''}`}
            onClick={() => handleFilterClick('not-reviewed')}
            title="Click to filter items not yet reviewed"
          >
            <span className="stat-icon">○</span>
            <span className="stat-count">{counts.notReviewed}</span>
            <span className="stat-label">Not Reviewed</span>
          </button>
          <div className="stat-divider" />
          <button
            className={`stat-item saveable ${statusFilter === 'saveable' ? 'active' : ''}`}
            onClick={() => handleFilterClick('saveable')}
            title="Click to filter items ready to save"
          >
            <span className="stat-icon">📋</span>
            <span className="stat-count">{counts.saveable}</span>
            <span className="stat-label">Ready to Save</span>
          </button>
          <button
            className={`stat-item saved ${statusFilter === 'saved' ? 'active' : ''}`}
            onClick={() => handleFilterClick('saved')}
            title="Click to filter items already saved to Q&A bank"
          >
            <span className="stat-icon">💾</span>
            <span className="stat-count">{counts.saved}</span>
            <span className="stat-label">Saved</span>
          </button>
          {statusFilter !== 'all' && (
            <button
              className="stat-item clear-filter"
              onClick={() => setStatusFilter('all')}
              title="Clear filter"
            >
              <span className="stat-icon">✕</span>
              <span className="stat-label">Clear</span>
            </button>
          )}
        </div>

        {/* Active filter indicator */}
        {statusFilter !== 'all' && (
          <div className="active-filter-indicator">
            Showing {filteredItems.length} {statusFilter} item{filteredItems.length !== 1 ? 's' : ''}
            <button onClick={() => setStatusFilter('all')}>Show all</button>
          </div>
        )}

        {/* Success message */}
        {saveSuccess && (
          <div className="save-success-message">{saveSuccess}</div>
        )}

        <div className="detail-meta">
          <span>📁 {questionnaire.original_filename}</span>
          <span>📊 {questionnaire.completed_items}/{questionnaire.total_items} items</span>
          {(questionnaire.uploaded_by_name || questionnaire.uploaded_by) && (
            <span title={questionnaire.uploaded_by}>
              👤 Uploaded by: {questionnaire.uploaded_by_name || questionnaire.uploaded_by}
            </span>
          )}
          {searchTerm && <span>🔍 Showing {filteredItems.length} of {questionnaire.items.length}</span>}
        </div>

        {/* Save to Q&A Modal */}
        {showSaveModal && (
          <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
            <div className="modal-content save-qa-modal" onClick={e => e.stopPropagation()}>
              <h3>💾 Save to Q&A Bank</h3>
              <p className="modal-description">
                Save <strong>{counts.saveable}</strong> approved/edited items to your Q&A knowledge bank.
                This will improve future responses.
              </p>
              <div className="form-group">
                <label>Customer Name (optional)</label>
                <input
                  type="text"
                  value={customerNameForSave}
                  onChange={(e) => setCustomerNameForSave(e.target.value)}
                  placeholder={questionnaire.client_name || 'Enter customer name...'}
                />
                <span className="input-hint">Leave blank to use "{questionnaire.client_name}"</span>
              </div>
              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowSaveModal(false)}
                  disabled={isSavingToQA}
                >
                  Cancel
                </button>
                <button
                  className="confirm-btn save-btn"
                  onClick={handleSaveToQABank}
                  disabled={isSavingToQA}
                >
                  {isSavingToQA ? 'Saving...' : `Save ${counts.saveable} Items`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Warning Modal */}
        {showExportWarning && exportWarningData && (
          <div className="modal-overlay" onClick={() => setShowExportWarning(false)}>
            <div className="modal-content export-warning-modal" onClick={e => e.stopPropagation()}>
              <h3>⚠️ Review Before Exporting</h3>
              <p className="modal-description">
                We noticed some items that may need your attention before exporting.
                <strong> Completing these will improve future AI responses!</strong>
              </p>

              <div className="warning-items-list">
                {exportWarningData.notReviewed > 0 && (
                  <div className="warning-item not-reviewed">
                    <div className="warning-item-header">
                      <span className="warning-icon">○</span>
                      <span className="warning-count">{exportWarningData.notReviewed}</span>
                      <span className="warning-label">Not Reviewed</span>
                    </div>
                    <p className="warning-description">
                      These items haven't been reviewed. Approve or edit them to ensure accuracy.
                    </p>
                    <button
                      className="warning-action-btn"
                      onClick={() => handleWarningAction('not-reviewed')}
                    >
                      Review Now →
                    </button>
                  </div>
                )}

                {exportWarningData.uncertain > 0 && (
                  <div className="warning-item uncertain">
                    <div className="warning-item-header">
                      <span className="warning-icon">?</span>
                      <span className="warning-count">{exportWarningData.uncertain}</span>
                      <span className="warning-label">Marked "Not Sure"</span>
                    </div>
                    <p className="warning-description">
                      These need further review. Research and update them for better future answers.
                    </p>
                    <button
                      className="warning-action-btn"
                      onClick={() => handleWarningAction('uncertain')}
                    >
                      Review Now →
                    </button>
                  </div>
                )}

                {exportWarningData.saveable > 0 && (
                  <div className="warning-item saveable">
                    <div className="warning-item-header">
                      <span className="warning-icon">📋</span>
                      <span className="warning-count">{exportWarningData.saveable}</span>
                      <span className="warning-label">Ready to Save</span>
                    </div>
                    <p className="warning-description">
                      Save these to your Q&A bank to train the AI and get better responses next time!
                    </p>
                    <button
                      className="warning-action-btn save-action"
                      onClick={() => {
                        setShowExportWarning(false);
                        setShowSaveModal(true);
                      }}
                    >
                      💾 Save to Q&A Bank
                    </button>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowExportWarning(false)}
                >
                  Go Back
                </button>
                <button
                  className="export-anyway-btn"
                  onClick={performExport}
                >
                  Export Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {questionnaire.status === 'processing' && (
          <div className="progress-section">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${getProgress()}%` }} />
            </div>
            <span>{getProgress()}% complete</span>
          </div>
        )}
        <div className="items-container">
          {filteredItems.map(item => (
            <QuestionnaireItemRow
              key={item.id}
              item={item}
              projectId={projectId}
              customFields={questionnaire.custom_fields || []}
              urlMappings={urlMappings}
              onSave={handleSaveItem}
              onReprocess={handleReprocess}
              onUpdateSources={handleUpdateSources}
              onAddToQA={handleAddToQA}
              onApprovalChange={(itemId, isApproved) => {
                // Update local state when approval changes
                setQuestionnaire(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    items: prev.items.map(i =>
                      i.id === itemId
                        ? { ...i, is_approved: isApproved, approved_by: user?.email, approved_by_name: user?.fullName }
                        : i
                    ),
                  };
                });
              }}
              isSaving={savingItems.has(item.id)}
              isAddedToQA={addedToQAItems.has(item.id)}
              currentUserEmail={user?.email}
              currentUserName={user?.fullName || user?.email}
            />
          ))}
          {filteredItems.length === 0 && searchTerm && (
            <div className="no-results">No questions match your search</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <MainLayout
      activeSection="questionnaires"
      onSectionChange={() => {}}
      projectName={projectName}
      projectId={projectId}
      username={displayName}
      userRole={userRole}
      subscriptionTier={subscriptionTier}
      onLogout={handleLogout}
    >
      {renderContent()}
    </MainLayout>
  );
};

export default QuestionnaireDetailPage;

